import React, { useState } from 'react';
import type { Candidate, MfaPhoneNumber, User } from '@axiom/validation';
import { isEnvoyAdmin, isLpUser } from '@axiom/utils';
import {
  Card,
  CardHeader,
  CardSection,
  Well,
  Button,
  SmallHeader,
  ParagraphHeader,
  CondensedMedium,
  Gutter,
  useApi,
  LayoutItem,
  Layout,
  IconButton,
  ToastUtil,
  CondensedLarge,
  Banner,
  Flashy,
} from '@axiom/ui';
import { AuthConst } from '@axiom/const';

import { CandidateApi } from '../../api/candidate-api';

import { PreferencesMfaPhoneActivate } from './PreferencesMfaPhoneActivate';
import { PreferencesMfaPhoneAddModal } from './PreferencesMfaPhoneAddModal';
import { PreferencesMfaPhoneVerifyModal } from './PreferencesMfaPhoneVerifyModal';
import { PreferencesMfaPhoneDeleteModal } from './PreferencesMfaPhoneDeleteModal';

export type PreferencesMfaPhoneProps = {
  name?: string;
  candidate: Candidate;
  user: User;
};

export const PreferencesMfaPhone = ({
  name = 'PREFERENCES_MFA_PHONE',
  candidate,
  user,
}: PreferencesMfaPhoneProps) => {
  const [{ data: mfa }] = useApi(
    CandidateApi.readCandidateMfaPhoneNumber(candidate.id)
  );

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tempMfa, setTempMfa] = useState<MfaPhoneNumber>({
    phoneNumberCountry: mfa.notificationsPhoneNumberCountry,
    phoneNumberCountryCode: mfa.notificationsPhoneNumberCountryCode,
    phoneNumber: mfa.notificationsPhoneNumber,
    registeredForMfa: null,
  });

  const fullPhoneNumber = `+${mfa.notificationsPhoneNumberCountryCode} ${mfa.notificationsPhoneNumber}`;
  const hasPhoneNumber =
    !!mfa.notificationsPhoneNumberCountry &&
    !!mfa.notificationsPhoneNumberCountryCode &&
    !!mfa.notificationsPhoneNumber;

  const restrictedCountries =
    AuthConst.IneligibleMultiFactorAuthenticationCountryCodes;

  const addRestrictedToast = (isDelete = false) => {
    ToastUtil.add({
      name: '2FA_RESTRICTION_TOAST',
      type: 'error',
      children: (
        <CondensedLarge>
          Only talent {isDelete ? 'and admin' : ''} can{' '}
          {isDelete ? 'delete' : 'edit'} 2FA phone numbers
        </CondensedLarge>
      ),
    });
  };

  return (
    <div data-test={name}>
      <Card>
        <CardHeader>
          <SmallHeader name="PREFERENCES_MFA_PHONE_HEADER">
            Text alerts and 2FA
          </SmallHeader>
        </CardHeader>
        <CardSection divider>
          {hasPhoneNumber && !isLpUser(user) ? (
            <Gutter bottom="8px">
              <Banner name="RESTRICTION_BANNER">
                <Flashy bold>
                  Only talent can edit or delete 2FA phone numbers.
                </Flashy>{' '}
                If talent has been locked out of their account, please ask an
                admin to remove the phone number on their behalf.
              </Banner>
            </Gutter>
          ) : null}
          <ParagraphHeader>
            Preferred phone number for text message alerts and 2FA
          </ParagraphHeader>
          <Gutter bottom="16px" />
          <CondensedMedium>
            Axiom will use this number to send security codes and other Axiom
            communications via SMS, based on your communications settings below.
          </CondensedMedium>
          <Gutter bottom="16px" />
          {hasPhoneNumber ? (
            <Well name="PHONE_INFO">
              <Layout position="middle space-between" name="PHONE_INFO">
                <LayoutItem>{fullPhoneNumber}</LayoutItem>
                <LayoutItem>
                  <IconButton
                    name="EDIT_PHONE_BUTTON"
                    icon="pencil"
                    variation="minimal"
                    onClick={() => {
                      if (isLpUser(user)) {
                        setIsAddModalOpen(true);
                      } else {
                        addRestrictedToast();
                      }
                    }}
                  />

                  <IconButton
                    name="DELETE_PHONE_BUTTON"
                    icon="trash"
                    variation="minimal"
                    onClick={() => {
                      if (isLpUser(user) || isEnvoyAdmin(user)) {
                        setIsDeleteModalOpen(true);
                      } else {
                        addRestrictedToast(true);
                      }
                    }}
                  />
                </LayoutItem>
              </Layout>
            </Well>
          ) : null}
          {!hasPhoneNumber && isLpUser(user) ? (
            <Well name="ADD_PHONE">
              <Button
                name="ADD_PHONE_BUTTON"
                onClick={() => {
                  setIsAddModalOpen(true);
                }}
              >
                Add phone number
              </Button>
            </Well>
          ) : null}
          {!hasPhoneNumber && !isLpUser(user) ? (
            <Well background="blue" name="PHONE_UNAVAILABLE">
              Talent has not provided a phone number for two-factor
              authentication
            </Well>
          ) : null}
        </CardSection>
        {hasPhoneNumber &&
        !restrictedCountries.includes(mfa.notificationsPhoneNumberCountry) ? (
          <CardSection>
            <PreferencesMfaPhoneActivate
              candidateId={candidate.id}
              mfa={mfa}
              user={user}
            />
          </CardSection>
        ) : null}
      </Card>
      {isAddModalOpen ? (
        <PreferencesMfaPhoneAddModal
          mfaPhoneNumber={mfa}
          userId={user.id}
          onClose={() => {
            setIsAddModalOpen(false);
          }}
          onAfterSubmit={() => {
            setIsAddModalOpen(false);
            setIsVerifyModalOpen(true);
          }}
          watchPhoneNumber={value => {
            setTempMfa(value);
          }}
        />
      ) : null}
      {isVerifyModalOpen ? (
        <PreferencesMfaPhoneVerifyModal
          userId={user.id}
          candidateId={candidate.id}
          tempMfa={tempMfa}
          onClose={() => {
            setIsVerifyModalOpen(false);
          }}
        />
      ) : null}
      {isDeleteModalOpen ? (
        <PreferencesMfaPhoneDeleteModal
          candidateId={candidate.id}
          onClose={() => {
            setIsDeleteModalOpen(false);
          }}
        />
      ) : null}
    </div>
  );
};
